import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Tutorials.css';

const Tutorials = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const videoIdParam = searchParams.get('video');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [videos, setVideos] = useState([]);
  const [singleVideo, setSingleVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadCounts, setDownloadCounts] = useState({});
  const [shareToast, setShareToast] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (videoIdParam) {
      fetchSingleVideo(videoIdParam);
    } else {
      setSingleVideo(null);
    }
  }, [videoIdParam]);

  useEffect(() => {
    if (selectedCategory !== null && !videoIdParam) {
      fetchVideos();
    }
  }, [selectedCategory, videoIdParam]);

  useEffect(() => {
    if (videos.length > 0) {
      fetchDownloadCounts();
    }
  }, [videos]);

  useEffect(() => {
    if (singleVideo) {
      Promise.all([
        axios.get(`/api/downloads?video_id=${singleVideo.id}`),
        axios.get(`/api/ai-launcher?video_id=${singleVideo.id}`)
      ])
        .then(([downloadsRes, aiLauncherRes]) => {
          const dLen = Array.isArray(downloadsRes.data) ? downloadsRes.data.length : 0;
          const aLen = Array.isArray(aiLauncherRes.data) ? aiLauncherRes.data.length : 0;
          setDownloadCounts((prev) => ({ ...prev, [singleVideo.id]: dLen + aLen }));
        })
        .catch(() => {});
    }
  }, [singleVideo?.id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories?table=videos');
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('获取分类列表失败:', error);
      setLoading(false);
    }
  };

  const fetchSingleVideo = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/videos/${id}`);
      const video = response.data;
      setSingleVideo(video);
      if (video.category) setSelectedCategory(video.category);
    } catch (error) {
      console.error('获取视频失败:', error);
      setSingleVideo(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const url = selectedCategory ? `/api/videos?category=${encodeURIComponent(selectedCategory)}` : '/api/videos';
      const response = await axios.get(url);
      setVideos(response.data);
    } catch (error) {
      console.error('获取视频列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDownloadCounts = async () => {
    const counts = {};
    for (const video of videos) {
      try {
        const [downloadsRes, aiLauncherRes] = await Promise.all([
          axios.get(`/api/downloads?video_id=${video.id}`),
          axios.get(`/api/ai-launcher?video_id=${video.id}`)
        ]);
        const dLen = Array.isArray(downloadsRes.data) ? downloadsRes.data.length : 0;
        const aLen = Array.isArray(aiLauncherRes.data) ? aiLauncherRes.data.length : 0;
        counts[video.id] = dLen + aLen;
      } catch (error) {
        counts[video.id] = 0;
      }
    }
    setDownloadCounts(counts);
  };

  const getBilibiliEmbedUrl = (bvid) => {
    // B站嵌入播放器URL（备用方案），设置默认最高画质1080p
    return `https://player.bilibili.com/player.html?bvid=${bvid}&autoplay=0&page=1&quality=80`;
  };

  const extractIframeProps = (embedCode) => {
    // 从嵌入代码中提取iframe属性
    if (!embedCode) return null;
    
    // 提取src
    const srcMatch = embedCode.match(/src=["']([^"']+)["']/);
    if (!srcMatch) return null;
    
    let src = srcMatch[1];
    // 处理相对协议URL（//开头）
    if (src.startsWith('//')) {
      src = `https:${src}`;
    } else if (!src.startsWith('http')) {
      src = `https://${src}`;
    }
    
    // 如果是Bilibili播放器URL，确保添加最高画质参数
    if (src.includes('player.bilibili.com')) {
      // 检查是否已有quality参数
      if (!src.includes('quality=')) {
        src += (src.includes('?') ? '&' : '?') + 'quality=80';
      } else {
        // 如果已有quality参数，替换为最高画质
        src = src.replace(/quality=\d+/, 'quality=80');
      }
    }
    
    return {
      src: src,
      // 提取其他属性
      scrolling: embedCode.match(/scrolling=["']([^"']+)["']/)?.[1] || 'no',
      frameBorder: embedCode.match(/frameBorder=["']([^"']+)["']/)?.[1] || 'no',
      allowFullScreen: embedCode.includes('allowfullscreen') || embedCode.includes('allowFullScreen')
    };
  };

  const openBilibili = (bvid) => {
    if (bvid) {
      window.open(`https://www.bilibili.com/video/${bvid}`, '_blank');
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setVideos([]);
    setSearchParams({});
  };

  const getVideoShareUrl = (videoId) => {
    return `${window.location.origin}/tutorials?video=${videoId}`;
  };

  const handleShareVideo = async (videoId) => {
    const url = getVideoShareUrl(videoId);
    try {
      await navigator.clipboard.writeText(url);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2500);
    } catch {
      prompt('复制链接分享', url);
    }
  };

  // 单个视频分享页（URL 带 ?video=123）
  if (videoIdParam) {
    const singleIframeProps = singleVideo && (singleVideo.embed_code
      ? extractIframeProps(singleVideo.embed_code)
      : (singleVideo.bvid ? { src: getBilibiliEmbedUrl(singleVideo.bvid), scrolling: 'no', frameBorder: 'no', allowFullScreen: true } : null));
    return (
      <div className="tutorials-container fade-in">
        {shareToast && (
          <div className="share-toast">链接已复制到剪贴板，可分享给好友</div>
        )}
        <div className="page-header">
          <button onClick={() => setSearchParams({})} className="back-btn">
            ← 返回视频列表
          </button>
        </div>
        {loading ? (
          <div className="loading">加载中...</div>
        ) : singleVideo ? (
          <div className="video-card single-video-card">
            <div className="video-player-container">
              {singleIframeProps ? (
                <iframe
                  src={singleIframeProps.src}
                  scrolling="no"
                  border="0"
                  frameBorder="no"
                  allowFullScreen
                  className="video-iframe"
                  title={singleVideo.title}
                />
              ) : (
                <div className="video-placeholder">
                  <p>视频加载失败</p>
                  {singleVideo.bvid && (
                    <a href={`https://www.bilibili.com/video/${singleVideo.bvid}`} target="_blank" rel="noopener noreferrer" className="action-btn primary">在B站打开</a>
                  )}
                </div>
              )}
            </div>
            <div className="video-info">
              <h3 className="video-title">{singleVideo.title}</h3>
              {singleVideo.description && <p className="video-description">{singleVideo.description}</p>}
              <div className="video-actions">
                <button onClick={() => handleShareVideo(singleVideo.id)} className="action-btn share-btn">
                  📤 分享本视频
                </button>
                {singleVideo.bvid && (
                  <a href={`https://www.bilibili.com/video/${singleVideo.bvid}`} target="_blank" rel="noopener noreferrer" className="action-btn primary">在B站打开</a>
                )}
                {(downloadCounts[singleVideo.id] || 0) > 0 && (
                  <button onClick={() => navigate(`/downloads?video_id=${singleVideo.id}`)} className="action-btn secondary">
                    下载资源 ({downloadCounts[singleVideo.id]}，含资源中心与AI创作助手)
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state"><p>未找到该视频</p></div>
        )}
      </div>
    );
  }

  return (
    <div className="tutorials-container fade-in">
      {shareToast && (
        <div className="share-toast">
          链接已复制到剪贴板，可分享给好友
        </div>
      )}
      <div className="page-header">
        <h1 className="page-title">视频教程</h1>
        <p className="page-subtitle">来自B站的技术分享视频</p>
      </div>

      {loading ? (
        <div className="loading">加载中...</div>
      ) : selectedCategory === null ? (
        // 显示分类列表
        categories.length === 0 ? (
          <div className="empty-state">
            <p>暂无分类</p>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category, index) => (
              <div 
                key={index} 
                className="category-card"
                onClick={() => handleCategoryClick(category)}
              >
                <h3 className="category-title">{category}</h3>
                <p className="category-description">点击查看该分类下的教程</p>
              </div>
            ))}
          </div>
        )
      ) : (
        // 显示选中分类下的视频
        <>
          <div className="category-header">
            <button onClick={handleBackToCategories} className="back-btn">
              ← 返回分类列表
            </button>
            <h2 className="selected-category-title">{selectedCategory}</h2>
          </div>
          {videos.length === 0 ? (
            <div className="empty-state">
              <p>该分类下暂无教程视频</p>
            </div>
          ) : (
        <div className="videos-grid">
          {videos.map((video) => {
            // 优先使用嵌入代码，如果没有则使用BV号生成
            const iframeProps = video.embed_code 
              ? extractIframeProps(video.embed_code) 
              : (video.bvid ? { src: getBilibiliEmbedUrl(video.bvid), scrolling: 'no', frameBorder: 'no', allowFullScreen: true } : null);
            
            return (
              <div key={video.id} className="video-card">
                <div className="video-player-container">
                  {iframeProps ? (
                    <iframe
                      src={iframeProps.src}
                      scrolling={iframeProps.scrolling}
                      border="0"
                      frameBorder={iframeProps.frameBorder}
                      framespacing="0"
                      allowFullScreen={iframeProps.allowFullScreen}
                      className="video-iframe"
                      title={video.title}
                    ></iframe>
                  ) : (
                    <div className="video-placeholder">
                      <p>视频加载失败，请检查嵌入代码或BV号</p>
                      {video.bvid && (
                        <a 
                          href={`https://www.bilibili.com/video/${video.bvid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn primary"
                        >
                          在B站打开
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="video-info">
                  <h3
                    className="video-title video-title-link"
                    onClick={() => setSearchParams({ video: video.id })}
                    title="点击查看本视频独立页面"
                  >
                    {video.title}
                  </h3>
                  {video.description && (
                    <p className="video-description">{video.description}</p>
                  )}
                  <div className="video-actions">
                    <button
                      onClick={() => handleShareVideo(video.id)}
                      className="action-btn share-btn"
                    >
                      📤 分享
                    </button>
                    {video.bvid && (
                      <a 
                        href={`https://www.bilibili.com/video/${video.bvid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn primary"
                      >
                        在B站打开
                      </a>
                    )}
                    {(downloadCounts[video.id] || 0) > 0 && (
                      <button
                        onClick={() => navigate(`/downloads?video_id=${video.id}`)}
                        className="action-btn secondary"
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--primary-color)';
                          e.target.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.color = 'var(--primary-color)';
                        }}
                        title="展示该视频关联的全部资源（资源中心+AI创作助手）"
                      >
                        下载资源 ({downloadCounts[video.id]})
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
          )}
        </>
      )}
    </div>
  );
};

export default Tutorials;

